(() => {
  if (customElements.get('sf-payment-options')) return;

  class SfPaymentOptions extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.copy = JSON.parse(this.querySelector('[data-sf-payment-copy]').textContent);
      this.planBody = this.querySelector('[data-sf-plan-body]');
      this.provider = 'affirm';
      this.affirmSummary = this.querySelector('[data-sf-summary-quote]').textContent;
      this.terms = [];
      this.cache = new Map();
      this.addEventListener('change', event => {
        if (event.target.matches('[data-sf-mode], [data-sf-term]')) event.stopPropagation();
      }, { signal });
      this.querySelectorAll('[data-sf-mode]').forEach(input => {
        input.addEventListener('change', () => this.selectPaymentMode(input.value, true), { signal });
      });
      this.querySelectorAll('[data-sf-provider]').forEach(button => {
        button.addEventListener('click', () => this.selectProvider(button.dataset.sfProvider), { signal });
        button.addEventListener('keydown', event => {
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          const target = event.key === 'Home' ? 'affirm' : event.key === 'End' ? 'shop' : this.provider === 'affirm' ? 'shop' : 'affirm';
          this.selectProvider(target);
          this.querySelector(`[data-sf-provider="${target}"]`).focus();
        }, { signal });
      });
      this.querySelector('[data-sf-term]').addEventListener('change', () => this.renderTerm(), { signal });
      this.querySelector('[data-sf-affirm-details]').addEventListener('click', event => {
        const trigger = this.querySelector('[data-sf-native-affirm] .affirm-modal-trigger');
        if (!trigger) return;
        event.preventDefault();
        trigger.click();
      }, { signal });
      this.querySelector('[data-sf-shop-plans]').addEventListener('click', () => {
        const component = this.querySelector('shopify-payment-terms');
        const trigger = component?.shadowRoot?.querySelector('button[aria-haspopup="dialog"]');
        if (trigger) {
          this.querySelector('[data-sf-shop-status]').textContent = '';
          trigger.click();
        } else {
          this.querySelector('[data-sf-shop-status]').textContent = this.copy.shopFallback;
        }
      }, { signal });
      this.nativeObserver = new MutationObserver(() => this.updateSummary());
      this.nativeObserver.observe(this.querySelector('[data-sf-native-affirm]'), { childList: true, characterData: true, subtree: true });
      this.updateSummary();
      this.section = this.closest('.shopify-section');
      this.section?.addEventListener('variant:update', event => this.updateVariant(event), { signal });
      this.shopForm = this.querySelector('.sf-payments__shop-form');
      this.shopButton = this.querySelector('[data-sf-shop-checkout]');
      if (this.shopButton) {
        this.shopForm.addEventListener('submit', event => this.checkoutWithShopPay(event), { signal });
        this.section?.addEventListener('input', () => this.updateShopAction(), { signal });
        this.section?.addEventListener('change', () => this.updateShopAction(), { signal });
      }
      this.selectProvider('affirm');
      this.restorePaymentMode();
      window.addEventListener('pageshow', event => {
        if (event.persisted) this.paymentIntent = undefined;
        this.restorePaymentMode();
      }, { signal });
      this.loadPlans();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.requestController?.abort();
      this.nativeObserver?.disconnect();
      this.shopRequestController?.abort();
      this.removeAttribute('data-shop-active');
    }

    restorePaymentMode() {
      this.nativeShopSubmission = false;
      this.shopSubmitting = false;
      if (this.shopButton) {
        this.shopButton.disabled = false;
        this.shopButton.textContent = this.copy.shopCheckout;
      }
      this.selectPaymentMode(window.sfPaymentPreference?.get(this.dataset.preferenceKey) || 'full');
    }

    selectPaymentMode(mode, remember = false) {
      this.mode = mode === 'installments' ? 'installments' : 'full';
      this.dataset.paymentMode = this.mode;
      this.querySelectorAll('[data-sf-mode]').forEach(input => { input.checked = input.value === this.mode; });
      this.planBody.hidden = this.mode !== 'installments';
      if (remember) window.sfPaymentPreference?.set(this.dataset.preferenceKey, this.mode);
      if (this.mode === 'installments') this.loadPlans();
      this.updateShopAction();
      this.savePaymentIntent();
    }

    selectProvider(provider) {
      this.provider = provider;
      this.querySelectorAll('[data-sf-provider]').forEach(button => {
        const selected = button.dataset.sfProvider === provider;
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      this.querySelectorAll('[data-sf-panel]').forEach(panel => {
        panel.hidden = panel.dataset.sfPanel !== provider;
      });
      if (provider === 'affirm') this.loadPlans();
      this.renderSummary();
      this.updateShopAction();
      this.savePaymentIntent();
    }

    savePaymentIntent() {
      if (this.hasAttribute('data-offer-preview') || !this.mode) return;
      const intent = this.hidden || this.mode !== 'installments' ? '' : this.provider === 'shop' ? 'shop_pay_installments' : 'affirm';
      if (intent === this.paymentIntent) return;
      this.paymentIntent = intent;
      this.intentRequest = window.sfPaymentPreference.saveIntent(intent);
      this.intentRequest.catch(() => {
        if (this.paymentIntent === intent) this.paymentIntent = undefined;
      });
    }

    productForm() {
      return this.section?.querySelector(`product-form-component[data-product-id="${CSS.escape(this.dataset.productId)}"] form[data-type="add-to-cart-form"]`);
    }

    updateShopAction() {
      if (!this.shopButton) return;
      const form = this.productForm();
      const submit = form?.querySelector('button[type="submit"]');
      const active = this.mode === 'installments' && this.provider === 'shop' &&
        this.dataset.shopCartEligible === 'true' && !this.shopUnavailable &&
        Boolean(form && submit && !submit.disabled);
      this.toggleAttribute('data-shop-active', active);
      this.shopButton.hidden = !active;
      const hint = this.querySelector(`#${CSS.escape(this.id)}-mode-hint`);
      hint.textContent = this.copy.shopHint;
      hint.hidden = !active;
    }

    async checkoutWithShopPay(event) {
      if (this.nativeShopSubmission) return;
      event.preventDefault();
      if (this.shopSubmitting || !this.hasAttribute('data-shop-active')) return;
      const form = this.productForm();
      if (!form?.reportValidity()) return;
      const fields = new FormData(form);
      const variantId = fields.get('id');
      const quantity = Number(fields.get('quantity') || form.closest('product-form-component').dataset.quantityDefault || 1);
      if (variantId !== this.dataset.variantId || !Number.isSafeInteger(quantity) || quantity < 1 || fields.get('selling_plan')) return;
      this.shopSubmitting = true;
      this.shopButton.disabled = true;
      this.shopButton.textContent = this.copy.shopContinuing;
      this.querySelector('[data-sf-shop-status]').textContent = '';
      this.shopRequestController?.abort();
      this.shopRequestController = new AbortController();
      const timer = window.setTimeout(() => this.shopRequestController.abort(), 10000);
      try {
        const endpoint = new URL(this.dataset.productUrl, window.location.origin);
        endpoint.search = new URLSearchParams({ section_id: this.dataset.sectionId, variant: variantId, country: 'US', currency: 'USD' });
        if (this.dataset.previewThemeId) endpoint.searchParams.set('preview_theme_id', this.dataset.previewThemeId);
        const response = await fetch(endpoint, { signal: this.shopRequestController.signal, credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error(this.copy.shopUnavailable);
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const current = page.querySelector(`sf-payment-options[data-block-id="${CSS.escape(this.dataset.blockId)}"]`);
        if (!current || !current.hasAttribute('data-direct-shop-pay') || current.dataset.variantId !== variantId || current.dataset.shopCartEligible !== 'true') {
          throw new Error(this.copy.shopCartUnavailable);
        }
        if (!this.isConnected || this.mode !== 'installments' || this.provider !== 'shop') return;
        const latest = new FormData(this.productForm());
        const latestQuantity = Number(latest.get('quantity') || quantity);
        if (latest.get('id') !== variantId || latestQuantity !== quantity || latest.get('selling_plan')) throw new Error(this.copy.shopUnavailable);
        const inputs = [];
        for (const [name, value] of fields) {
          if (['form_type', 'utf8', 'product-id', 'section-id', 'return_to', 'quantity'].includes(name) || typeof value !== 'string') continue;
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          input.value = value;
          inputs.push(input);
        }
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.value = String(quantity);
        inputs.push(input);
        this.querySelector('[data-sf-shop-fields]').replaceChildren(...inputs);
        this.nativeShopSubmission = true;
        this.shopButton.disabled = false;
        this.shopForm.requestSubmit(this.shopButton);
      } catch (error) {
        if (!this.isConnected) return;
        this.shopUnavailable = true;
        this.querySelector('[data-sf-shop-status]').textContent = error.message === this.copy.shopCartUnavailable ? this.copy.shopCartUnavailable : this.copy.shopUnavailable;
        this.updateShopAction();
      } finally {
        window.clearTimeout(timer);
        this.shopSubmitting = false;
        this.shopButton.disabled = false;
        this.shopButton.textContent = this.copy.shopCheckout;
      }
    }

    renderSummary() {
      this.querySelector('[data-sf-summary-quote]').textContent = this.hasAttribute('data-direct-shop-pay') && this.provider === 'shop' ? this.copy.shopSummary : this.affirmSummary;
    }

    updateSummary() {
      if (this.loadedKey) return;
      const price = this.querySelector('[data-sf-native-affirm] .affirm-ala-price')?.textContent.trim();
      if (price) this.affirmSummary = this.copy.from.replace('__amount__', price);
      this.renderSummary();
    }

    async loadPlans() {
      const amount = Number(this.dataset.amount);
      const cacheKey = `${this.dataset.variantId}:${amount}`;
      if (this.loadedKey === cacheKey || this.pendingKey === cacheKey) return;
      if (!Number.isSafeInteger(amount) || amount <= 0 || !this.dataset.publicKey) {
        this.querySelector('[data-sf-loading]').textContent = this.copy.unavailable;
        return;
      }
      this.pendingKey = cacheKey;
      this.querySelector('[data-sf-loading]').textContent = this.copy.loading;
      this.querySelector('[data-sf-loading]').hidden = false;
      this.requestController?.abort();
      const requestController = new AbortController();
      this.requestController = requestController;
      const timer = window.setTimeout(() => requestController.abort(), 10000);
      try {
        let data = this.cache.get(cacheKey);
        if (!data) {
          const endpoint = new URL(`https://www.affirm.com/api/promos/v2/${encodeURIComponent(this.dataset.publicKey)}`);
          endpoint.search = new URLSearchParams({
            amount: String(amount), field: 'modal', page_type: 'product', locale: 'en_US', use_best_terms: 'true',
            items: JSON.stringify([{ sku: this.dataset.sku, display_name: this.dataset.productTitle, qty: 1, unit_price: amount }])
          }).toString();
          const response = await fetch(endpoint, { credentials: 'omit', signal: requestController.signal });
          if (!response.ok) throw new Error('Promotional plans unavailable');
          data = await response.json();
          this.cache.set(cacheKey, data);
        }
        if (!this.isConnected || this.pendingKey !== cacheKey) return;
        const terms = Array.isArray(data.offer?.terms) ? data.offer.terms : [];
        this.terms = terms.filter(term =>
          Number.isSafeInteger(term.installment_count) && term.installment_count > 0 &&
          Number.isSafeInteger(term.installment_amount) && term.installment_amount > 0 &&
          Number.isSafeInteger(term.amount) && term.amount >= amount &&
          Number.isFinite(term.interest_amount) && term.interest_amount >= 0 &&
          Number.isFinite(term.apr) && term.apr >= 0
        );
        if (!this.terms.length) throw new Error('No sample plans returned');
        const selector = this.querySelector('[data-sf-term]');
        selector.replaceChildren(...this.terms.map((term, index) => {
          const option = document.createElement('option');
          option.value = String(index);
          option.textContent = (term.billing_frequency === 'monthly' ? this.copy.months : this.copy.payments).replace('__count__', String(term.installment_count));
          return option;
        }));
        const lowest = this.terms.reduce((best, term, index) => term.installment_amount < this.terms[best].installment_amount ? index : best, 0);
        selector.value = String(lowest);
        const monthly = this.terms.filter(term => term.billing_frequency === 'monthly');
        if (monthly.length) {
          const payment = Math.min(...monthly.map(term => term.installment_amount));
          this.affirmSummary = this.copy.from.replace('__amount__', this.money(payment));
          this.renderSummary();
        }
        this.querySelector('[data-sf-live-plans]').hidden = false;
        this.querySelector('[data-sf-loading]').hidden = true;
        this.renderTerm();
        this.renderLegal(data.promo?.html_footer || '');
        this.loadedKey = cacheKey;
      } catch (error) {
        if (!this.isConnected || this.pendingKey !== cacheKey) return;
        this.querySelector('[data-sf-loading]').textContent = this.copy.unavailable;
        this.querySelector('[data-sf-loading]').hidden = false;
      } finally {
        window.clearTimeout(timer);
        if (this.pendingKey === cacheKey) this.pendingKey = null;
      }
    }

    money(cents) {
      return new Intl.NumberFormat(this.dataset.locale || 'en-US', { style: 'currency', currency: this.dataset.currency }).format(cents / 100);
    }

    renderTerm() {
      const term = this.terms[Number(this.querySelector('[data-sf-term]').value)];
      if (!term) return;
      this.querySelector('[data-sf-payment-caption]').textContent = term.billing_frequency === 'monthly' ? this.copy.monthly : term.billing_frequency === 'biweekly' ? this.copy.biweekly : this.copy.payment;
      this.querySelector('[data-sf-installment]').textContent = this.money(term.installment_amount);
      this.querySelector('[data-sf-apr]').textContent = `${new Intl.NumberFormat(this.dataset.locale || 'en-US', { maximumFractionDigits: 2 }).format(term.apr)}%`;
      this.querySelector('[data-sf-interest]').textContent = this.money(term.interest_amount);
      this.querySelector('[data-sf-total]').textContent = this.money(term.amount);
      this.querySelector('[data-sf-downpayment-row]').hidden = !(term.downpayment_amount > 0);
      this.querySelector('[data-sf-downpayment]').textContent = this.money(term.downpayment_amount || 0);
    }

    renderLegal(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const allowed = new Set(['A', 'BR', 'P', 'SPAN', 'STRONG', 'EM', 'B', 'I', 'SMALL', 'UL', 'LI']);
      const link = (label, href) => {
        try {
          const url = new URL(href, 'https://www.affirm.com');
          if (href && url.protocol === 'https:' && (url.hostname === 'affirm.com' || url.hostname.endsWith('.affirm.com'))) {
            const anchor = document.createElement('a');
            anchor.textContent = label;
            anchor.href = url.href;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer';
            return anchor;
          }
        } catch (error) {}
        return document.createTextNode(label);
      };
      const text = value => {
        const fragment = document.createDocumentFragment();
        const pattern = /\[([^\]]+)\]\((https:\/\/[^\s)]+)\)/g;
        let offset = 0;
        let match;
        while ((match = pattern.exec(value))) {
          fragment.append(document.createTextNode(value.slice(offset, match.index)), link(match[1], match[2]));
          offset = pattern.lastIndex;
        }
        fragment.append(document.createTextNode(value.slice(offset)));
        return fragment;
      };
      const clean = node => {
        if (node.nodeType === Node.TEXT_NODE) return text(node.textContent);
        if (node.nodeType !== Node.ELEMENT_NODE) return document.createDocumentFragment();
        if (['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED'].includes(node.tagName)) return document.createDocumentFragment();
        if (node.tagName === 'A') return link(node.textContent, node.getAttribute('href'));
        const output = allowed.has(node.tagName) ? document.createElement(node.tagName.toLowerCase()) : document.createDocumentFragment();
        node.childNodes.forEach(child => output.append(clean(child)));
        return output;
      };
      const content = document.createDocumentFragment();
      parsed.body.childNodes.forEach(node => content.append(clean(node)));
      this.querySelector('[data-sf-legal]').replaceChildren(content);
    }

    updateVariant(event) {
      if (String(event.detail?.data?.productId) !== this.dataset.productId) return;
      const next = event.detail.data.html?.querySelector(`sf-payment-options[data-block-id="${CSS.escape(this.dataset.blockId)}"]`);
      if (!next) {
        this.hidden = true;
        this.requestController?.abort();
        this.savePaymentIntent();
        return;
      }
      this.hidden = false;
      this.savePaymentIntent();
      this.requestController?.abort();
      this.pendingKey = null;
      this.loadedKey = null;
      ['variantId', 'amount', 'sku', 'productId', 'productTitle'].forEach(key => { this.dataset[key] = next.dataset[key]; });
      this.querySelector('[data-sf-full-price]').textContent = next.querySelector('[data-sf-full-price]').textContent;
      this.querySelector('[data-sf-live-plans]').hidden = true;
      this.querySelector('[data-sf-legal]').replaceChildren();
      this.querySelector('[data-sf-summary-quote]').textContent = next.querySelector('[data-sf-summary-quote]').textContent;
      this.affirmSummary = next.querySelector('[data-sf-summary-quote]').textContent;
      this.dataset.shopCartEligible = next.dataset.shopCartEligible;
      this.querySelectorAll('.affirm-as-low-as').forEach(element => { element.dataset.amount = this.dataset.amount; });
      const input = this.querySelector('.sf-payments__shop-form input[name="id"]');
      input.value = this.dataset.variantId;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      this.querySelector('shopify-payment-terms')?.setAttribute('variant-id', this.dataset.variantId);
      if (typeof window.affirm?.ui?.refresh === 'function') window.affirm.ui.refresh();
      this.loadPlans();
      this.renderSummary();
      queueMicrotask(() => this.updateShopAction());
    }
  }

  customElements.define('sf-payment-options', SfPaymentOptions);
})();
